<template>
  <div class="gap-2 flex items-center" :style="indentStyle">
    <div v-if="fieldName==='patient' || params.dataType==='patient'">
      <font-awesome-icon :icon="['fas','user-injured']"/>
    </div>
    <div v-else-if="params.dataType==='metric_value' && params.node.field ==='performance_domain_name'" >
      <div class="border border-gray-700 rounded-md w-5 h-5"
          :style="{ backgroundColor: params.node.data.performance_domain_color }"
      >
      </div>
    </div>
    <div v-else-if="params.dataType==='metric_value'">
      <FontAwesomeIcon class="text-lg" :icon="icon"/>
    </div>
    <router-link v-if="fieldName !=='performance_domain'" :to="link">
      <span class="sensitive text-neutral-900 font-semibold hover:text-neutral-700 hover:underline">{{full_display_name}}</span>
    </router-link>
    <span v-else class="sensitive">{{ (displayText) }}</span>
  </div>
</template>

<script>
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {toProperCase} from "../../../utils/common";

export default {
  components: {FontAwesomeIcon},
  props: {},
  computed: {
    full_display_name() {
      const field_name = this.params.dataTypeName || this.params?.node?.field;
      const field_name_proper = toProperCase((field_name || '').replace(/_/g, ' '));
      const display_label = this.params.node.data?.type_name || field_name_proper;
      return `${display_label && display_label + ': '}${this.displayText}`;
    },
    icon(){
      if(this.params.node.field ==='metric_name'){
        return ['fas','chart-mixed'];
      }
      // this.params.node.data.icon json string to array
      else if(this.params.node.data.icon){
        try{
          return JSON.parse(this.params.node.data.icon);
        }
        catch (e) {
          return ['fas','circle'];
        }
      }
      else{
        return ['fas','chart-mixed'];
      }
    },
    indentStyle() {
      // Check if indentation is enabled via params
      if (this.params?.enableIndentation) {
        const indentPixels = this.groupLevel * 40; // 20px per level, adjust as needed
        return {
          paddingLeft: `${indentPixels}px`
        };
      }
      return {};
    },
    isGroup() {
      return this.params.node.group;
    },
    fieldName() {
      const key = this.params.node.key;
      if (!key) return 'unknown';
      const delimiterIndex = key.indexOf('|');
      return delimiterIndex !== -1 ? key.substring(0, delimiterIndex) : 'unknown';
    },
    displayText() {
      if (this.isGroup) {
        if (this.params.dataType === 'surveyresponse') {
          return ''
        }
        else if (this.params.node.field === 'entity_groups'){
          return this.params.valueFormatted
        }
        else if (this.params.node.field === 'contract_name'){
          return this.params.valueFormatted
        }
        else if (this.params.node.field === 'practice_owners'){
          return this.params.valueFormatted
        }
        else {
          const key = this.params.node.key
          if (!key) return 'Unknown'
          const delimiterIndex = key.indexOf('|')
          return delimiterIndex !== -1 ? key.substring(delimiterIndex + 1) : key
        }
      }
      else{
        if (this.params.dataType === 'engagement'){
          return this.params.value
        }
        else if (this.params.dataType === 'lead'){
          return this.params.value
        }
        else{
          const value = this.params.value;
          if (!value) return 'Unknown'
          const delimiterIndex = value.indexOf('|')
          return delimiterIndex !== -1 ? value.substring(delimiterIndex + 1) : value
        }
      }
    },
    groupLevel() {
      return this.params.node.level;
    },
    groupField() {
      const groupingFields = this.params.api
        .getRowGroupColumns()
        .map(col => col.getColId());
      return groupingFields[this.groupLevel];
    },
    groupIdField() {
      return `${this.groupField.replace('_name', '_id')}`;
    },
    groupId() {
      return this.params.node.aggData[this.groupIdField];
    },
    objectId() {
      if(this.params.object_id_custom_fieldname){
        return this.params.data[this.params.object_id_custom_fieldname]
      }
      else if (this.params.dataType === 'engagement'){
        return this.params.data.id
      }
      else if(this.params.dataType === 'patient') {
        return this.params.data.bene_mbi_id || this.params.data.other_patient_identifier
      }
      else{
        return this.params.data.object_id;
      }
    },
    objectContentTypeModel() {
      if (this.params.data?.content_type_model) {
        return this.params.data.content_type_model;
      } else {
        return '';
      }
    },
    link() {
      if (this.params.dataType === 'entity') {
        if (this.objectContentTypeModel === 'engagement') {
          return `/engagement/${this.objectId}`;
        } else if (this.objectContentTypeModel === 'survey') {
          return `/surveys/${this.id}`;
        }
      }
      else if (this.params.dataType === 'practice_portal'){
        return `/portal/practice/${this.params.node.aggData['practice_id']}`;
      }
      else if (this.params.dataType === 'patient') {
        if (!this.isGroup) {
          return `/patients/${this.params.data.patient_identifier || this.objectId}`;
        } else {
          return `/patients/${this.params.node.aggData.patient_identifier}`;
        }
      }
      else if (this.params.dataType === 'metric_value') {
        let metric_id = null;

        if (!this.isGroup) {
          metric_id = this.params.data['metric_id']
        }
        else {
          metric_id = this.params.node?.data?.metric_id;
        }

        if(this.params.dataTypeSecondary === 'entity_id' && this.params.data.entity_id){
          const entity_id = this.params.data.entity_id;
          return `/insight/metric/${metric_id}/${entity_id}`;
        }
        else{
          return `/insight/metric/${metric_id}/`;
        }

      }
      else if (this.params.dataType === 'engagement'){
        return `/engagement/${this.objectId}`
      }
      else if (this.params.dataType ==='survey'){
        return `/surveys/${this.params.data.id}`
      }
      else if (this.params.dataType ==='contract'){
        if(!this.isGroup){
          return `/contract/${this.params.data.contract_id}`
        }
        else{
          return `/contract/${this.params.node.aggData.contract_id}`
        }
      }
      else if (this.params.dataType ==='teamsmeeting'){
        return `/microsoftteams/meetings/${this.params.data.id}/appuser/${this.params.appuserId}`
      }
      else if (this.params.dataType === 'lead'){
        return `/lead/${encodeURIComponent(this.params.data.practice_lbn)}`
      }
      else if (this.params.dataType === 'surveyresponse'){
        return `/surveys/${this.params.data?.id}`
      }
      else if (this.params.dataType === 'alertinstance'){
        return `/metricalertinstance/${this.params.data?.id}`
      }



      return '';
    },
  },
  methods: {
    toProperCase,
    randomizeString(str) {
      return str
        .split('')
        .sort(() => 0.5 - Math.random())
        .join('');
    },
  },
  mounted() {
  },
};
</script>

<style scoped>


</style>
